import {
  Activity,
  AlertTriangle,
  FileText,
  Gauge,
  LayoutDashboard,
  LogOut,
  Radar,
  ScanLine,
  Settings,
  Truck,
  User,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ROUTES } from '../config/routes';
import { logoutThunk } from '../redux/slices/authSlice';
import { useAppDispatch, useAppSelector } from '../redux/store';

interface Props {
  children: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  Icon: typeof LayoutDashboard;
  staffOnly?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'Composters',
    items: [
      { to: ROUTES.DASHBOARD, label: 'Dashboard', Icon: LayoutDashboard },
      { to: ROUTES.INSIGHTS, label: 'Insights', Icon: Activity },
      { to: ROUTES.DIAGNOSTICS, label: 'Diagnostics', Icon: Gauge },
    ],
  },
  {
    title: 'Lifters',
    items: [{ to: ROUTES.LIFTER_DASHBOARD, label: 'Dashboard', Icon: Truck }],
  },
  {
    title: 'Operations',
    items: [
      { to: ROUTES.ALARMS, label: 'Alarms', Icon: AlertTriangle },
      { to: ROUTES.REPORTS, label: 'Reports', Icon: FileText },
      { to: ROUTES.SEGMENTOR, label: 'AI Segmentor', Icon: ScanLine },
    ],
  },
  {
    title: 'Admin',
    items: [
      { to: ROUTES.ADMIN_USERS, label: 'Users', Icon: Users, staffOnly: true },
      { to: ROUTES.ADMIN_MACHINES, label: 'Machines', Icon: Settings, staffOnly: true },
      { to: ROUTES.ADMIN_SENSORS, label: 'Sensors', Icon: Radar, staffOnly: true },
    ],
  },
  {
    title: 'Account',
    items: [{ to: ROUTES.PROFILE, label: 'Profile', Icon: User }],
  },
];

const AppLayout = ({ children }: Props) => {
  const user = useAppSelector((s) => s.auth.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onLogout = async () => {
    await dispatch(logoutThunk());
    navigate(ROUTES.LOGIN, { replace: true });
  };

  const initials = (user?.firstname?.[0] ?? user?.username?.[0] ?? '?').toUpperCase();
  const isStaff = user?.tfstaff ?? false;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="flex w-64 flex-col border-r border-slate-200 bg-white">
        <div className="flex h-16 items-center gap-2 border-b border-slate-200 px-5">
          <div className="flex size-8 items-center justify-center rounded-lg bg-tfs-primary text-sm font-semibold text-white">
            T
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold text-slate-900">TFSIQ</span>
            <span className="text-[11px] text-slate-500">Terraforma Systems</span>
          </div>
        </div>

        <nav className="flex flex-1 flex-col gap-5 overflow-y-auto p-3">
          {navSections.map((section) => {
            const visibleItems = section.items.filter((i) => !i.staffOnly || isStaff);
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.title}>
                <p className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {section.title}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {visibleItems.map(({ to, label, Icon }) => (
                    <li key={to}>
                      <NavLink
                        to={to}
                        end
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors ${
                            isActive
                              ? 'bg-tfs-primary/10 font-medium text-tfs-primary'
                              : 'text-slate-600 hover:bg-slate-100'
                          }`
                        }
                      >
                        <Icon className="size-4 shrink-0" aria-hidden />
                        {label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-2 rounded-md p-2">
            <div className="flex size-8 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700">
              {initials}
            </div>
            <div className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-sm font-medium text-slate-800">
                {user?.firstname || user?.username || '—'}
              </span>
              <span className="truncate text-[11px] text-slate-500">
                {isStaff ? 'TFS staff' : 'Member'}
              </span>
            </div>
            <button
              type="button"
              onClick={onLogout}
              title="Log out"
              aria-label="Log out"
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            >
              <LogOut className="size-4" aria-hidden />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <main className="flex-1 overflow-auto p-8">
          <div className="mx-auto max-w-[1400px]">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
